import { useMemo } from "react";
import { addDays, differenceInCalendarWeeks, parseISO, startOfWeek } from "date-fns";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { ARCHIVED_OUT } from "#app/widgets/dashboards/use-dimension-slices";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { timeSeriesGroupBy, type TimeSeriesMeasure } from "#app/lib/data";
import { at } from "#app/lib/utils/at";

/** Which timestamp the heatmap buckets by. */
export type HeatMetric = TimeSeriesMeasure;
/** One heatmap cell (a day within a week), its issue count, and its calendar date. */
export type HeatBin = { bin: number; count: number; date: Date };
/** One heatmap column (a calendar week) and its seven day bins. */
export type HeatColumn = { bin: number; bins: HeatBin[] };

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Buckets exact server-side per-day counts (`groupCounts` with the
 * `<measure>.day` grain — computed in Postgres in the viewer's calendar) into a
 * day-of-week × week matrix (the visx heatmap `Bins[]` shape: one column per
 * calendar week, seven day bins each) — a GitHub-style activity calendar of when
 * work was created or last touched. Exact over the whole scoped window, never a
 * capped client sample (B6): a Bartlett-scale board with thousands of issues in
 * the window counts them all. The date window follows the chosen `metric`, and
 * only the most recent `weeks` columns are kept.
 */
export function useActivityHeatmap(metric: HeatMetric, weeks: number) {
  const { where } = useScopedWhere(ARCHIVED_OUT, {
    dateField: metric === "updated" ? "lastUpdateDate" : "creationDate",
  });
  const { counts, ready, error } = useIssueGroupCounts({
    where,
    groupBy: timeSeriesGroupBy(metric, "day"),
  });

  const result = useMemo(() => {
    // Bucket keys are `YYYY-MM-DD` in the viewer's calendar; parseISO reads a
    // date-only string as LOCAL midnight, so day-of-week math stays local.
    const cells = Object.entries(counts)
      .map(([key, count]) => ({ date: parseISO(key), count }))
      .filter((c) => !Number.isNaN(c.date.getTime()));

    if (cells.length === 0) {
      return { columns: [] as HeatColumn[], max: 0, dayLabels: DAY_LABELS, loading: !ready };
    }

    const times = cells.map((c) => c.date.getTime());
    const minT = Math.min(...times);
    const maxT = Math.max(...times);
    // Align week 0 to the Sunday on/before the earliest date. Calendar-week math
    // (not a fixed 7×24h constant) so DST-length weeks don't drift the buckets.
    const start = startOfWeek(minT, { weekStartsOn: 0 });

    const totalWeeks = differenceInCalendarWeeks(maxT, start, { weekStartsOn: 0 }) + 1;
    const firstWeek = Math.max(0, totalWeeks - Math.max(1, weeks));

    const grid: number[][] = Array.from({ length: totalWeeks - firstWeek }, () => Array(7).fill(0));
    for (const c of cells) {
      const week = differenceInCalendarWeeks(c.date, start, { weekStartsOn: 0 });
      if (week < firstWeek) continue;
      const row = at(grid, week - firstWeek, "heatmap week");
      const day = c.date.getDay();
      row[day] = at(row, day, "heatmap day") + c.count;
    }

    let max = 0;
    const columns: HeatColumn[] = grid.map((week, wi) => ({
      bin: wi,
      bins: week.map((count, di) => {
        if (count > max) max = count;
        // The cell's calendar day = week-0 Sunday + (actual week × 7) + day-of-week.
        return { bin: di, count, date: addDays(start, (firstWeek + wi) * 7 + di) };
      }),
    }));

    return { columns, max, dayLabels: DAY_LABELS, loading: !ready };
  }, [counts, weeks, ready]);

  return { ...result, error };
}
